import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../web/components-v2/Combobox';
import { DateRangePicker } from '../../../../web/components-v2/DateRangePicker';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../../web/components/UserCombobox';
import { ExportIcon } from '../../../../web/components/svgs/icons/ExportIcon';
import { RefreshIcon } from '../../../../web/components/svgs/icons/RefreshIcon';
import { ViewportContext } from '../../../hooks/useViewport';
import { ALL_SDRS, CallLogUrlFilters } from '../callLogFilters';
import { MoreFiltersPopover } from './MoreFiltersPopover';
import { OutcomeFilter } from './OutcomeFilter';
import { SecondaryFilterFields } from './SecondaryFilterFields';
import { styleSheet } from './styles';

interface IProps {
	filters: CallLogUrlFilters;
	condensed: boolean;
	showExport: boolean;
	showResetFilters: boolean;
	isExporting: boolean;
	onChange: (next: Partial<CallLogUrlFilters>) => void;
	onResetSecondary: () => void;
	onResetFilters: () => void;
	onExport: () => void;
}

export const CallLogToolbar: React.FC<IProps> = ({
	filters,
	condensed,
	showExport,
	showResetFilters,
	isExporting,
	onChange,
	onResetSecondary,
	onResetFilters,
	onExport,
}) => {
	const id = React.useId();
	const { isBelowSmall } = React.useContext(ViewportContext);
	const sdrId = filters.sdr && filters.sdr !== ALL_SDRS ? filters.sdr : undefined;

	const resetButton = showResetFilters ? (
		<Button type='button' variant='secondary' onClick={onResetFilters}>
			<RefreshIcon width={16} height={16} fillColor='currentColor' />
			Reset filters
		</Button>
	) : null;
	const exportButton = showExport ? (
		<div className={css(styleSheet.export)}>
			<Button type='button' variant='secondary' isLoading={isExporting} onClick={onExport}>
				<ExportIcon fillColor='currentColor' />
				Export report
			</Button>
		</div>
	) : null;

	return (
		<div className={css(styleSheet.toolbar)}>
			<div className={css(styleSheet.controls)}>
				<div className={css(styleSheet.sdr)}>
					<UserCombobox
						id={sdrId}
						key={`sdr-${filters.sdr ?? ALL_SDRS}`}
						onValueChange={(user: IUser | null) => onChange({ sdr: user?.id ?? ALL_SDRS })}
					>
						<ComboboxInput id={`${id}-sdr`} aria-label='SDR' placeholder='All SDRs' showClear />
						<ComboboxContent>
							<UserComboboxStatus />
							<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
						</ComboboxContent>
					</UserCombobox>
				</div>
				<div className={css(styleSheet.dateRange)}>
					<DateRangePicker
						ariaLabel='Call date'
						from={filters.from ?? ''}
						to={filters.to ?? ''}
						size='sm'
						numberOfMonths={isBelowSmall ? 1 : 2}
						onChange={(from, to) => onChange({ from: from || null, to: to || null })}
					/>
				</div>
				<div className={css(styleSheet.outcome)}>
					<OutcomeFilter outcomes={filters.outcomes} onChange={outcomes => onChange({ outcomes })} />
				</div>
				{condensed ? <MoreFiltersPopover filters={filters} onChange={onChange} onReset={onResetSecondary} /> : null}
				{condensed ? resetButton : null}
			</div>
			{condensed ? (
				exportButton
			) : (
				<>
					<div className={css(styleSheet.inlineFilters)}>
						<SecondaryFilterFields filters={filters} enabled onChange={onChange} />
					</div>
					{showResetFilters || showExport ? (
						<div className={css(styleSheet.inlineActions)}>
							{resetButton}
							{exportButton}
						</div>
					) : null}
				</>
			)}
		</div>
	);
};
